import BooleanWithLabel from "@/Components/Boolean/BooleanWithLabel";
import Button from "@/Components/Button/Button";
import Textfield from "@/Components/Input/Textfield";
import Modal, { ModalProps } from "@/Components/Modal/Modal";
import { screenUiLanguages } from "@/Components/Screen/ScreenLanguageProvider";
import Select from "@/Components/Select/Select";
import usePickupScreenSettings, {
  defaultPickupScreenSettings,
  type PickupScreenLayout,
  type PickupScreenSettings,
} from "@/shared/hooks/usePickupScreenSettings";
import { fontFamilyOptions } from "@/shared/utils/constant";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

const selectMenuProps = {
  menuPlacement: "bottom" as const,
  menuPortalTarget: document.getElementById("root"),
  styles: {
    menuPortal: (base: object) => ({
      ...base,
      zIndex: 1000000,
    }),
  },
};

const PickupScreenSettingsModal = ({ onExit, ...props }: ModalProps) => {
  const { t } = useTranslation();
  const { settings: savedSettings, setSettings: persistSettings } =
    usePickupScreenSettings();
  const [settings, setSettings] =
    useState<PickupScreenSettings>(savedSettings);

  const layoutOptions = [
    { value: "vertical", label: t("receipt_page.vertical") },
    { value: "horizontal", label: t("receipt_page.horizontal") },
  ];

  const sortOptions = [
    { value: "receipt_number", label: t("pickupScreenSettings.sortReceipt") },
    { value: "oldest_first", label: t("pickupScreenSettings.sortOldest") },
    { value: "newest_first", label: t("pickupScreenSettings.sortNewest") },
  ];

  const cardSizeOptions = [
    { value: "small", label: t("pickupScreenSettings.cardSizeSmall") },
    { value: "medium", label: t("pickupScreenSettings.cardSizeMedium") },
    { value: "large", label: t("pickupScreenSettings.cardSizeLarge") },
  ];

  const patch = <K extends keyof PickupScreenSettings>(
    key: K,
    value: PickupScreenSettings[K]
  ) => setSettings((prev) => ({ ...prev, [key]: value }));

  const onSave = () => {
    persistSettings({ ...defaultPickupScreenSettings, ...settings });
    toast.success(t("pickupScreenSettings.saved"));
    onExit?.();
  };

  return (
    <Modal width={{ _: "95%", sm: "64rem" }} onExit={onExit} {...props}>
      <div className="px-8 pt-12">
        <h2 className="mt-0 mb-2 text-[2.4rem] font-black">
          {t("pickupScreenSettings.title")}
        </h2>
        <p className="mb-8 text-text/60">
          {t("pickupScreenSettings.subtitle")}
        </p>

        <div className="mb-6 grid grid-cols-1 gap-6 md:grid-cols-2">
          <Textfield
            type="number"
            label={t("fontSize")}
            value={settings.font_size}
            onChange={(e) => patch("font_size", Number(e.target.value) || 0)}
          />

          <Select
            {...selectMenuProps}
            label={t("cms_users.language")}
            options={screenUiLanguages}
            value={screenUiLanguages.find((l) => l.value === settings.language)}
            onChange={(option: { value: string }) =>
              patch("language", option.value)
            }
            getOptionLabel={(option) => t(`languages.${option.label}`)}
            getOptionValue={(option) => option.value}
          />

          <Select
            {...selectMenuProps}
            label={t("pickupScreenSettings.layout")}
            options={layoutOptions}
            value={layoutOptions.find((o) => o.value === settings.layout)}
            onChange={(option: { value: PickupScreenLayout }) =>
              patch("layout", option.value)
            }
            getOptionLabel={(option) => option.label}
            getOptionValue={(option) => option.value}
          />

          <Select
            {...selectMenuProps}
            label={t("pickupScreenSettings.fontFamily")}
            options={fontFamilyOptions}
            value={fontFamilyOptions.find(
              (o) => o.value === settings.font_family
            )}
            onChange={(option: { value: string }) =>
              patch("font_family", option.value)
            }
            getOptionLabel={(option) => option.label}
            getOptionValue={(option) => option.value}
          />

          <Select
            {...selectMenuProps}
            label={t("pickupScreenSettings.cardSize")}
            options={cardSizeOptions}
            value={cardSizeOptions.find((o) => o.value === settings.card_size)}
            onChange={(option: { value: PickupScreenSettings["card_size"] }) =>
              patch("card_size", option.value)
            }
            getOptionLabel={(option) => option.label}
            getOptionValue={(option) => option.value}
          />

          <Select
            {...selectMenuProps}
            label={t("pickupScreenSettings.sortBy")}
            options={sortOptions}
            value={sortOptions.find((o) => o.value === settings.sort_by)}
            onChange={(option: { value: PickupScreenSettings["sort_by"] }) =>
              patch("sort_by", option.value)
            }
            getOptionLabel={(option) => option.label}
            getOptionValue={(option) => option.value}
          />

          <Textfield
            type="number"
            label={t("pickupScreenSettings.maxOrders")}
            value={settings.max_orders_per_section}
            onChange={(e) =>
              patch("max_orders_per_section", Number(e.target.value) || 0)
            }
          />
        </div>

        <div className="mb-8 grid grid-cols-1 gap-4 md:grid-cols-2">
          <BooleanWithLabel
            checked={settings.show_customer_name}
            onChange={(e) => patch("show_customer_name", e.target.checked)}
          >
            {t("pickupScreenSettings.showCustomerName")}
          </BooleanWithLabel>

          <BooleanWithLabel
            checked={settings.show_order_age}
            onChange={(e) => patch("show_order_age", e.target.checked)}
          >
            {t("pickupScreenSettings.showOrderAge")}
          </BooleanWithLabel>

          <BooleanWithLabel
            checked={settings.show_preparing_section}
            onChange={(e) =>
              patch("show_preparing_section", e.target.checked)
            }
          >
            {t("pickupScreenSettings.showPreparing")}
          </BooleanWithLabel>

          <BooleanWithLabel
            checked={settings.show_ready_section}
            onChange={(e) => patch("show_ready_section", e.target.checked)}
          >
            {t("pickupScreenSettings.showReady")}
          </BooleanWithLabel>

          <BooleanWithLabel
            checked={settings.sound_on_ready}
            onChange={(e) => patch("sound_on_ready", e.target.checked)}
          >
            {t("pickupScreenSettings.soundOnReady")}
          </BooleanWithLabel>

          <BooleanWithLabel
            checked={settings.confirm_on_handover}
            onChange={(e) => patch("confirm_on_handover", e.target.checked)}
          >
            {t("pickupScreenSettings.confirmHandover")}
          </BooleanWithLabel>
        </div>

        <Button onClick={onSave}>{t("save")}</Button>
      </div>
    </Modal>
  );
};

export default PickupScreenSettingsModal;
